import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { APIProvider as GoogleMapsProvider } from "@vis.gl/react-google-maps";
import { AppSidebar } from "@/components/universal/sidebar/app-sidebar";
import { SidebarInset } from "@/components/ui/sidebar";
import { useAuth } from "@/auth/use-auth";
import { Footer } from "@/components/universal/footer";
import { messaging } from "@/auth/firebase";
import { isSupported, onMessage } from "firebase/messaging";
import { useEffect } from "react";
import { toast } from "sonner";
import { TwilioDeviceProvider } from "@/contexts/twilio-device-context";
import { useTwilioContext } from "@/hooks/use-twilio-context";
import { ActiveCallIndicator } from "@/components/calling/active-call-indicator";
import { CommandPalette } from "@/components/universal/command-palette";
import {
  getGetNotificationsQueryKey,
  useMarkNotificationAsRead,
} from "@/requests/gen/react-query/notification";
import { queryClient } from "@/lib/query-client";
import { PWAInstallPrompt } from "@/pwa-install-prompt";
import { useQueryState, parseAsInteger } from "nuqs";
import ForceMfaEnrollment from "./-force-mfa-enrollment";
import { Timeout } from "../-timeout";

export const Route = createFileRoute("/_private")({
  component: PrivateLayout,
});

function PrivateLayout() {
  const { authUser } = useAuth();

  // Redirect to the login page if there is no user
  if (!authUser) {
    return (
      <Navigate
        to="/auth/login"
        search={{ next: location.pathname + location.search }}
        replace
      />
    );
  }

  // Force MFA enrollment before anything else when the user's entity requires it
  if (authUser.mfa_required && !authUser.mfa_enabled) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <ForceMfaEnrollment />
      </div>
    );
  }

  toast.dismiss("timeout-logout");

  return (
    <MaybeMapsProvider>
      <TwilioDeviceProvider>
        <PrivateLayoutContent />
      </TwilioDeviceProvider>
    </MaybeMapsProvider>
  );
}

function MaybeMapsProvider({ children }: { children: React.ReactNode }) {
  if (import.meta.env.MODE !== "prod") return <>{children}</>;
  return (
    <GoogleMapsProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
      {children}
    </GoogleMapsProvider>
  );
}

function PrivateLayoutContent() {
  const { authUser } = useAuth();
  const navigate = useNavigate();

  const [notificationId, setNotificationId] = useQueryState(
    "notification_id",
    parseAsInteger,
  );

  // Hooks for notifications
  const { mutate: markNotificationAsRead } = useMarkNotificationAsRead();

  // Get call status and errors for page-level handling
  const { callStatus, error: deviceError } = useTwilioContext();

  // Show device errors as toasts
  useEffect(() => {
    if (deviceError) {
      toast.error(`Call error: ${deviceError}`, {
        duration: 5000,
      });
    }
  }, [deviceError]);

  // Prevent page close/refresh during active calls
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only prevent if there's an active call (not idle or disconnected)
      if (callStatus !== "idle" && callStatus !== "disconnected") {
        e.preventDefault();
        return "";
      }
    };

    if (callStatus !== "idle" && callStatus !== "disconnected") {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [callStatus]);

  // FCM foreground messages
  useEffect(() => {
    const setupMessaging = async () => {
      // Check if Firebase Messaging is supported
      const isMessagingSupported = await isSupported();

      if (isMessagingSupported && messaging) {
        const unsubscribe = onMessage(messaging, (payload) => {
          const to = payload.data?.["to"];
          const notificationId = payload.data?.["notification_id"];
          if (to) {
            if (to.startsWith("/texting")) {
              // Refetch conversation history
              queryClient.invalidateQueries({
                queryKey: ["api", "text_message"],
                exact: false,
              });
            }

            // Refetch notifications
            queryClient.invalidateQueries({
              queryKey: getGetNotificationsQueryKey(),
            });

            // Show toast
            toast(payload.notification?.title, {
              description: payload.notification?.body,
              action: {
                label: "View",
                onClick: () => {
                  navigate({
                    to,
                    search: notificationId
                      ? { notification_id: Number(notificationId) }
                      : undefined,
                  });
                },
              },
            });
          }
        });

        return () => {
          if (typeof unsubscribe === "function") {
            unsubscribe();
          }
        };
      } else {
        console.log("Firebase Messaging is not supported in this browser.");
      }
    };

    setupMessaging();
  }, [navigate]);

  // Mark notifications as read
  useEffect(() => {
    if (!notificationId || !authUser?.user_id) return;

    markNotificationAsRead(
      { notificationId: notificationId, asRead: true },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetNotificationsQueryKey(),
          });
          // Remove the query param from the URL
          setNotificationId(null);
        },
      },
    );
  }, [
    authUser?.user_id,
    markNotificationAsRead,
    notificationId,
    setNotificationId,
  ]);

  return (
    <>
      <AppSidebar variant="inset" />
      <SidebarInset className="min-w-0">
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <Outlet />
          <Footer />
          <PWAInstallPrompt />
        </div>
      </SidebarInset>

      {/* Call Management (Patient Advocates only) */}
      <ActiveCallIndicator />
      <CommandPalette />
      <Timeout />
    </>
  );
}
